import {
  useRecordContext,
  SimpleShowLayout,
  Labeled,
} from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Link,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { Variant } from '@src/types';
import { DashboardResources } from '@src/types/constants';

// Component for a single variant group (colorway) showing SKU listing
const VariantGroupItem = ({ variant }: { variant: Variant }) => {
  const navigate = useNavigate();

  return (
    <Accordion sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {variant.colorDisplay}
          </Typography>
          <Chip
            label={`${variant.variantCount || variant.sizeDisplays?.length || 0} variants`}
            size="small"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            SKUs in this color:
          </Typography>
          {variant.sizeDisplays && variant.skus ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {variant.sizeDisplays.map((size: string, index: number) => {
                const sku = variant.skus[index];
                if (!sku) return null;
                return (
                  <Box key={sku} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">
                      {size}
                      :
                      {' '}
                      {sku}
                    </Typography>
                    <Link
                      component="button"
                      variant="caption"
                      type="button"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void navigate(`/${DashboardResources.VariantSkus}/${sku}`);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      (Edit)
                    </Link>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2">No SKU data available</Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const VariantsSection = () => {
  const record = useRecordContext();

  if (!record?.variants || !Array.isArray(record.variants)) {
    return (
      <SimpleShowLayout>
        <Labeled label="">
          <span>No variants available</span>
        </Labeled>
      </SimpleShowLayout>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Expand each color variant to view SKUs
      </Typography>
      {record.variants.map((variant: Variant, index: number) => {
        const colorCode = variant.colorCode || `variant-${index}`;
        return (
          <VariantGroupItem
            key={colorCode}
            variant={variant}
          />
        );
      })}
    </Box>
  );
};

export default VariantsSection;
